/**
 * Client-Side Caching Layer with Offline Resilience
 *
 * Implements a robust caching strategy using IndexedDB for persistence:
 *
 * Strategy: Stale-While-Revalidate
 * - Serve cached data immediately (if available)
 * - Fetch fresh data in background
 * - Update cache with fresh data
 * - Notify subscribers of updates
 *
 * Benefits:
 * - Instant load times (no API wait)
 * - Offline functionality (works without internet)
 * - Graceful degradation (API errors don't break UI)
 * - Automatic cache invalidation (configurable TTL)
 *
 * Cache Keys:
 * - `units:${userId}:${companyIds}:${groupIds}` - Unit list by user and filters (prevents RBAC bypass)
 * - `unit:${unitId}` - Individual unit details
 * - `companies:${userId}` - User's accessible companies
 * - `groups:${companyId}` - Groups for a company
 */

import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'hollywoodtrucks-cache';
const DB_VERSION = 3; // Bumped for terminology refactor (trucks→units, accounts→companies)
const STORE_NAME = 'cache';

/**
 * Cache TTL (Time To Live) - how long cached data is considered fresh.
 *
 * These numbers are the Map/Reports refresh rate for FLEET STRUCTURE, not just a
 * performance knob. A fresh entry short-circuits the network entirely (see
 * staleWhileRevalidate below), so a TTL of 24h meant that reassigning a unit to a
 * production in Settings left the Map, the filter sidebar and Reports showing the
 * OLD production for a full day on every device that had already loaded once —
 * while Settings → Fleet, which reads the uncached /admin/units, showed the new
 * one. Assignments change many times a day; keep these in minutes, not hours.
 */
export const CACHE_TTL = {
  units: 60 * 1000,                  // 1 minute — assignment/status churn is constant
  unit: 30 * 1000,                   // 30 seconds for individual unit (real-time data)
  companies: 5 * 60 * 1000,          // 5 minutes
  groups: 5 * 60 * 1000,             // 5 minutes — a new production must be filterable today
};

interface CacheEntry<T> {
  key: string;
  data: T;
  timestamp: number;
  expiresAt: number;
}

type CacheType = 'units' | 'unit' | 'companies' | 'groups';

class CacheManager {
  private db: IDBPDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private subscribers: Map<string, Set<(data: unknown) => void>> = new Map();

  /**
   * Initialize IndexedDB connection
   */
  private async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      try {
        this.db = await openDB(DB_NAME, DB_VERSION, {
          upgrade(db) {
            // Create object store if it doesn't exist
            if (!db.objectStoreNames.contains(STORE_NAME)) {
              const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
              store.createIndex('expiresAt', 'expiresAt');
            }
          },
        });

        // Clean up expired entries on init
        await this.cleanupExpired();
      } catch (error) {
        console.error('[Cache] Failed to initialize IndexedDB:', error);
        // Fallback: Continue without cache (in-memory only)
      }
    })();

    return this.initPromise;
  }

  /**
   * Get data from cache
   * Returns null if not found or expired
   */
  async get<T>(key: string): Promise<T | null> {
    await this.init();
    if (!this.db) return null;

    try {
      const entry = await this.db.get(STORE_NAME, key) as CacheEntry<T> | undefined;

      if (!entry) {
        return null;
      }

      // Check if expired
      if (Date.now() > entry.expiresAt) {
        // Don't delete yet - stale data is better than no data
        // Mark as stale but still return it
        console.log(`[Cache] ${key} is stale, will revalidate`);
        return entry.data;
      }

      return entry.data;
    } catch (error) {
      console.error('[Cache] Get error:', error);
      return null;
    }
  }

  /**
   * Set data in cache with TTL
   */
  async set<T>(key: string, data: T, type: CacheType): Promise<void> {
    await this.init();
    if (!this.db) return;

    const ttl = CACHE_TTL[type];
    const now = Date.now();

    const entry: CacheEntry<T> = {
      key,
      data,
      timestamp: now,
      expiresAt: now + ttl,
    };

    try {
      await this.db.put(STORE_NAME, entry);

      // Notify subscribers of update
      this.notifySubscribers(key, data);
    } catch (error) {
      console.error('[Cache] Set error:', error);
    }
  }

  /**
   * Delete a specific cache entry
   */
  async delete(key: string): Promise<void> {
    await this.init();
    if (!this.db) return;

    try {
      await this.db.delete(STORE_NAME, key);
    } catch (error) {
      console.error('[Cache] Delete error:', error);
    }
  }

  /**
   * Delete every entry whose key starts with `prefix`.
   *
   * Cache keys are namespaced per user and per filter combination
   * (`units:${userId}:${companyIds}:${groupIds}`), so invalidating "the unit
   * list" after a mutation means sweeping a prefix — there is no single key to
   * delete. Returns how many entries were removed.
   */
  async deleteByPrefix(prefix: string): Promise<number> {
    await this.init();
    if (!this.db) return 0;

    try {
      const keys = await this.db.getAllKeys(STORE_NAME);
      const matching = keys.filter(
        (key): key is string => typeof key === 'string' && key.startsWith(prefix)
      );

      for (const key of matching) {
        await this.db.delete(STORE_NAME, key);
      }

      return matching.length;
    } catch (error) {
      console.error('[Cache] deleteByPrefix error:', error);
      return 0;
    }
  }

  /**
   * Clear all cache entries
   */
  async clear(): Promise<void> {
    await this.init();
    if (!this.db) return;

    try {
      await this.db.clear(STORE_NAME);
    } catch (error) {
      console.error('[Cache] Clear error:', error);
    }
  }

  /**
   * Clean up expired entries
   * Called on init and periodically
   */
  private async cleanupExpired(): Promise<void> {
    if (!this.db) return;

    try {
      const tx = this.db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('expiresAt');

      const now = Date.now();
      const expiredKeys = await index.getAllKeys(IDBKeyRange.upperBound(now));

      for (const key of expiredKeys) {
        await store.delete(key);
      }

      await tx.done;

      if (expiredKeys.length > 0) {
        console.log(`[Cache] Cleaned up ${expiredKeys.length} expired entries`);
      }
    } catch (error) {
      console.error('[Cache] Cleanup error:', error);
    }
  }

  /**
   * Subscribe to cache updates for a specific key
   * Useful for reactive UI updates when cache is refreshed
   */
  subscribe<T>(key: string, callback: (data: T) => void): () => void {
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, new Set());
    }

    this.subscribers.get(key)!.add(callback as (data: unknown) => void);

    // Return unsubscribe function
    return () => {
      const subs = this.subscribers.get(key);
      if (subs) {
        subs.delete(callback as (data: unknown) => void);
        if (subs.size === 0) {
          this.subscribers.delete(key);
        }
      }
    };
  }

  /**
   * Notify all subscribers of a cache update
   */
  private notifySubscribers<T>(key: string, data: T): void {
    const subs = this.subscribers.get(key);
    if (subs) {
      subs.forEach(callback => callback(data));
    }
  }

  /**
   * Check if cache entry exists and is fresh
   */
  async isFresh(key: string): Promise<boolean> {
    await this.init();
    if (!this.db) return false;

    try {
      const entry = await this.db.get(STORE_NAME, key);
      return entry ? Date.now() <= entry.expiresAt : false;
    } catch {
      return false;
    }
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<{ count: number; size: number }> {
    await this.init();
    if (!this.db) return { count: 0, size: 0 };

    try {
      const entries = await this.db.getAll(STORE_NAME);
      const size = JSON.stringify(entries).length;
      return { count: entries.length, size };
    } catch {
      return { count: 0, size: 0 };
    }
  }
}

// Singleton instance
export const cache = new CacheManager();

/**
 * Cache key generators
 *
 * IMPORTANT: Cache keys MUST include user ID where applicable to prevent
 * cross-user cache pollution (RBAC bypass via shared cache keys).
 */
export const cacheKeys = {
  // CRITICAL: userId MUST be included to prevent RBAC bypass via shared cache
  // Without userId, a superadmin's cached data could leak to restricted users
  units: (userId: string, companyIds: string[], groupIds: string[]) =>
    `units:${userId}:${companyIds.sort().join(',')}:${groupIds.sort().join(',')}`,

  unit: (unitId: string) =>
    `unit:${unitId}`,

  companies: (userId: string) =>
    `companies:${userId}`,

  groups: (companyId: string) =>
    `groups:${companyId}`,

  // Bulk groups cache - includes userId for RBAC security, sorted companyIds for consistent hits
  groupsBulk: (userId: string, companyIds: string[]) =>
    `groups:bulk:${userId}:${companyIds.sort().join(',')}`,
};

/**
 * Drop everything the Map / filter sidebar / Reports read about fleet structure.
 *
 * Call this from the WRITE side — after a unit is reassigned, created or deleted,
 * or after a group/company changes. Settings pages fetch through `adminApi`, which
 * is uncached, so without this the acting user sees their own change land in
 * Settings while every cached read path keeps serving the pre-mutation snapshot
 * until its TTL lapses.
 *
 * Sweeps by prefix because keys are namespaced per user and per filter combo.
 */
export async function invalidateFleetCaches(): Promise<void> {
  await Promise.all([
    cache.deleteByPrefix('units:'),
    cache.deleteByPrefix('unit:'),
    cache.deleteByPrefix('groups:'),
    cache.deleteByPrefix('companies:'),
  ]);
}

/**
 * In-flight fetches, keyed by cache key.
 *
 * The unit list is requested by both the Map page and FilterContext on the same
 * mount, and the shorter TTLs above mean revalidation now happens on most loads
 * rather than once a day. Without this, that is two identical /units round trips
 * (~1,500 units each) every time.
 */
const inFlight = new Map<string, Promise<unknown>>();

function dedupedFetch<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
  const existing = inFlight.get(key) as Promise<T> | undefined;
  if (existing) return existing;

  const promise = fetchFn();
  inFlight.set(key, promise);
  // Both handlers supplied, so this chain never surfaces an unhandled rejection;
  // the caller owns error handling on the promise we return.
  promise.then(
    () => inFlight.delete(key),
    () => inFlight.delete(key)
  );
  return promise;
}

/**
 * Check if data is empty (null, undefined, or empty array)
 * Used to avoid caching empty results which likely indicate temporary state
 */
function isEmptyResult<T>(data: T): boolean {
  if (data === null || data === undefined) return true;
  if (Array.isArray(data) && data.length === 0) return true;
  return false;
}

/**
 * Fetches data using a stale-while-revalidate strategy.
 *
 * Attempts to return cached data immediately when available. If the cached entry is stale,
 * returns the stale value and triggers a background fetch to update the cache. If no cached
 * entry exists, fetches fresh data, stores it in the cache, and returns it. If a fetch fails
 * but stale data exists, the stale data is returned as a fallback; if no cache exists the
 * error is re-thrown.
 *
 * IMPORTANT: Empty arrays are NOT cached for 'companies' and 'groups' types because:
 * - Empty results usually indicate temporary state (new user, pending membership)
 * - Caching empty arrays can cause persistent "0 companies" issues
 * - Users should always get fresh data to see newly assigned memberships
 *
 * @param key - Cache key.
 * @param fetchFn - Function that fetches fresh data when revalidation or a cache miss is required.
 * @param type - Cache type used to determine TTL behavior.
 * @param displayLabel - Human-readable label used in cache logs.
 * @param onRevalidated - Called with the fresh payload when a BACKGROUND revalidation
 *   lands. Without it the caller keeps rendering the stale value it was handed and
 *   only picks up the truth on a later page load — which is how a reassigned unit
 *   could sit on the wrong production on the Map even after the fetch had corrected
 *   the cache. Not called on the cache-miss path: there the return value IS fresh.
 * @returns The cached or freshly fetched data for the given key.
 */
export async function staleWhileRevalidate<T>(
  key: string,
  fetchFn: () => Promise<T>,
  type: CacheType,
  displayLabel?: string,
  onRevalidated?: (data: T) => void
): Promise<T> {
  // Use displayLabel for readable logs, fallback to key
  const logKey = displayLabel ? `${key.split(':')[0]}: ${displayLabel}` : key;
  // Try to get cached data first
  const cachedData = await cache.get<T>(key);
  const isFresh = await cache.isFresh(key);

  // CRITICAL: For companies and groups, don't trust empty cached results
  // Empty arrays likely indicate temporary state (new user, pending membership)
  // Always fetch fresh data when cache is empty for these types
  const shouldIgnoreEmptyCache = (type === 'companies' || type === 'groups') && isEmptyResult(cachedData);

  // If we have fresh cache with actual data, return it and skip fetch
  if (cachedData && isFresh && !shouldIgnoreEmptyCache) {
    console.log(`[Cache] HIT (fresh): ${logKey}`);
    return cachedData;
  }

  // If we have stale cache with actual data, return it but revalidate in background
  if (cachedData && !isFresh && !shouldIgnoreEmptyCache) {
    console.log(`[Cache] HIT (stale): ${logKey}, revalidating...`);

    // Return stale data immediately
    // Fetch fresh data in background (don't await), then hand it to the caller so
    // the view it already painted can correct itself in place.
    dedupedFetch(key, fetchFn)
      .then(freshData => {
        // Only cache non-empty results for companies/groups
        if ((type === 'companies' || type === 'groups') && isEmptyResult(freshData)) {
          console.log(`[Cache] Skipping cache for empty ${type} result: ${logKey}`);
          return;
        }
        cache.set(key, freshData, type);
        onRevalidated?.(freshData);
      })
      .catch(error => {
        console.error(`[Cache] Background revalidation failed for ${logKey}:`, error);
        // Keep stale data in cache as fallback
      });

    return cachedData;
  }

  // No cache (or empty cache for companies/groups) - fetch fresh data
  console.log(`[Cache] ${shouldIgnoreEmptyCache ? 'IGNORING EMPTY CACHE' : 'MISS'}: ${logKey}, fetching...`);
  try {
    const freshData = await dedupedFetch(key, fetchFn);

    // Only cache non-empty results for companies/groups
    if ((type === 'companies' || type === 'groups') && isEmptyResult(freshData)) {
      console.log(`[Cache] Skipping cache for empty ${type} result: ${logKey}`);
      return freshData;
    }

    await cache.set(key, freshData, type);
    return freshData;
  } catch (error) {
    console.error(`[Cache] Fetch failed for ${logKey}:`, error);

    // If fetch fails and we have stale data (even empty), return it as fallback
    if (cachedData) {
      console.log(`[Cache] Using stale data as fallback for ${logKey}`);
      return cachedData;
    }

    // No cache and fetch failed - re-throw error
    throw error;
  }
}
